/**
 * Basecamp Sidebar Panel Component
 * Legacy integration sidebar (Bookmarks + My Stuff summary + Projects).
 * NOT mounted in production left nav - used by test-page.tsx and integration registry only.
 * Canonical assignment UI: Todos tab (basecamp-section-view TodosPanel).
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome';
import {
  Plus,
  Eye,
  EyeOff,
  Pin,
  Settings,
  RefreshCw,
  AlertCircle,
  Search,
  X,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useBasecampProjects, useBasecampProjectStats } from '@/hooks/use-basecamp';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { StorageKeys, StorageSchemas } from '@/lib/storage';
import { BasecampMyStuffSidebar } from './basecamp-my-stuff-sidebar';
import { BasecampIcon } from '@/components/icons/basecamp-icon';
import { resolveOpenExternalIcon } from '@/lib/open-destination-icons';
import { BASECAMP_OPEN_IN_BASECAMP_LABEL } from '@/lib/basecamp-todo-open-labels';
import { BookmarksWidget } from './components/bookmarks-widget';
import type { BasecampProject } from '@/types/basecamp';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import type { IntegrationSidebarProps } from '@/types/integration';

interface BasecampSidebarProps extends IntegrationSidebarProps {
  onProjectSelect?: (projectId: string) => void;
}

export function BasecampSidebar({ onProjectSelect, onAction }: BasecampSidebarProps) {
  const { projects, isLoading, error, refetch } = useBasecampProjects();
  const [hiddenProjects, setHiddenProjects] = usePersistedState<string[]>({
    key: StorageKeys.BASECAMP_HIDDEN_PROJECTS,
    defaultValue: [],
    schema: StorageSchemas.stringArray,
  });
  const [pinnedProjects, setPinnedProjects] = usePersistedState<string[]>({
    key: StorageKeys.BASECAMP_PINNED_PROJECTS,
    defaultValue: [],
    schema: StorageSchemas.stringArray,
  });
  const [isLoadingPreferences, setIsLoadingPreferences] = React.useState(false);
  const [isProjectsExpanded, setIsProjectsExpanded] = usePersistedState({
    key: StorageKeys.BASECAMP_PROJECTS_EXPANDED,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  });
  const [searchQuery, setSearchQuery] = React.useState('');
  // Persisted panel sizes for resizable widgets (must be called before early returns)
  const [widgetSizes, setWidgetSizes] = usePersistedState<number[]>({
    key: StorageKeys.BASECAMP_SIDEBAR_WIDGET_SIZES,
    defaultValue: [30, 35, 35], // Bookmarks, My Stuff, Projects (default sizes)
    schema: StorageSchemas.basecampWidgetSizes,
  });

  // Filter projects based on search query
  const filteredProjects = React.useMemo(() => {
    if (!projects) return [];

    // If searching, show ALL projects (including hidden) that match
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return projects.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }

    // If not searching, show only non-hidden projects
    return projects.filter(p => !hiddenProjects.includes(p.id));
  }, [projects, searchQuery, hiddenProjects]);

  const activeProjects = filteredProjects;
  const hidden = projects?.filter(p => hiddenProjects.includes(p.id)) || [];

  const handleProjectClick = (project: BasecampProject, event?: React.MouseEvent) => {
    const openInNewTab = event?.metaKey || event?.ctrlKey || false;

    // Create a stable tab ID for this project
    const tabId = `basecamp-project-${project.id}`;

    console.log(`[Basecamp] Project clicked:`, {
      projectName: project.name,
      tabId,
      openInNewTab
    });

    // Dispatch event with complete tab data
    window.dispatchEvent(new CustomEvent('integration-view-change', {
      detail: {
        tabId: tabId,
        title: project.name,
        type: 'integration-basecamp',
        integrationData: {
          integrationId: 'basecamp',
          projectId: project.id
        },
        openInNewTab: openInNewTab
      }
    }));

    // Also call the legacy callback for backward compatibility
    onProjectSelect?.(project.id);
  };

  const handleHideProject = (projectId: string) => {
    setHiddenProjects(prev => [...prev, projectId]);
  };

  const handleShowProject = (projectId: string) => {
    setHiddenProjects(prev => prev.filter(id => id !== projectId));
  };

  // Load project preferences from backend on mount and sync with localStorage
  React.useEffect(() => {
    const loadPreferences = async () => {
      try {
        setIsLoadingPreferences(true);
        console.log('[BasecampSidebar] Loading preferences from backend...');
        const { basecampApi } = await import('@/lib/basecamp-api');
        const preferences = await basecampApi.getProjectPreferences();

        console.log('[BasecampSidebar] Raw preferences from backend:', preferences);

        // Extract pinned project IDs from preferences
        // Ensure all IDs are strings for consistent comparison
        const pinnedIds = Object.entries(preferences)
          .filter(([_, prefs]) => prefs.isPinned)
          .map(([projectId]) => String(projectId)); // Convert to string for consistency

        console.log('[BasecampSidebar] Extracted pinned project IDs:', pinnedIds);
        console.log('[BasecampSidebar] Current localStorage pinned projects:', pinnedProjects);
        console.log('[BasecampSidebar] Available projects:', projects?.map(p => ({ id: p.id, idType: typeof p.id, name: p.name })));

        // Always sync with backend state (backend is source of truth)
        setPinnedProjects(pinnedIds);
        console.log('[BasecampSidebar] Updated pinnedProjects state to:', pinnedIds);
      } catch (err) {
        console.warn('[BasecampSidebar] Failed to load project preferences:', err);
        console.warn('[BasecampSidebar] Falling back to localStorage:', pinnedProjects);
        // Continue with localStorage state if backend fails
        // This allows offline functionality
      } finally {
        setIsLoadingPreferences(false);
      }
    };

    loadPreferences();
  }, []); // Only run on mount

  const handlePinProject = async (projectId: string) => {
    // Ensure projectId is a string for consistent comparison
    const projectIdStr = String(projectId);
    const wasPinned = pinnedProjects.includes(projectIdStr);
    const willBePinned = !wasPinned;

    console.log('[BasecampSidebar] ===== PIN TOGGLE START =====');
    console.log('[BasecampSidebar] Input projectId:', projectId, 'type:', typeof projectId);
    console.log('[BasecampSidebar] Normalized projectIdStr:', projectIdStr, 'type:', typeof projectIdStr);
    console.log('[BasecampSidebar] Current pinnedProjects:', pinnedProjects);
    console.log('[BasecampSidebar] pinnedProjects types:', pinnedProjects.map(id => ({ id, type: typeof id })));
    console.log('[BasecampSidebar] wasPinned check:', wasPinned, '(includes check:', pinnedProjects.includes(projectIdStr), ')');
    console.log('[BasecampSidebar] willBePinned:', willBePinned);

    // Optimistic update: Update UI immediately
    const newPinnedProjects = wasPinned
      ? pinnedProjects.filter(id => String(id) !== projectIdStr)
      : [...pinnedProjects.filter(id => String(id) !== projectIdStr), projectIdStr]; // Remove duplicates and add

    console.log('[BasecampSidebar] New pinnedProjects (optimistic):', newPinnedProjects);
    setPinnedProjects(newPinnedProjects);

    // Update backend
    try {
      console.log('[BasecampSidebar] Calling backend API to update preference...');
      const { basecampApi } = await import('@/lib/basecamp-api');
      await basecampApi.updateProjectPreference(projectIdStr, undefined, willBePinned);
      console.log('[BasecampSidebar] ✅ Backend API call successful');

      // Verify the update by reloading preferences
      const preferences = await basecampApi.getProjectPreferences();
      const pinnedFromBackend = Object.entries(preferences)
        .filter(([_, prefs]) => prefs.isPinned)
        .map(([id]) => String(id));
      console.log('[BasecampSidebar] Verified pinned projects from backend:', pinnedFromBackend);
      console.log('[BasecampSidebar] Current state:', newPinnedProjects);
      console.log('[BasecampSidebar] Match:', JSON.stringify(pinnedFromBackend.sort()) === JSON.stringify(newPinnedProjects.sort()));
    } catch (err) {
      console.error('[BasecampSidebar] ❌ Failed to update pin preference:', err);
      console.error('[BasecampSidebar] Error details:', {
        message: err instanceof Error ? err.message : 'Unknown',
        status: (err as any)?.status,
        response: (err as any)?.response
      });

      // Rollback on error
      console.log('[BasecampSidebar] Rolling back to previous state:', pinnedProjects);
      setPinnedProjects(pinnedProjects);

      const { toast } = await import('sonner');
      toast.error('Failed to update pin', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    }
    console.log('[BasecampSidebar] ===== PIN TOGGLE END =====');
  };

  const handleSyncProject = (projectId: string) => {
    onAction?.('sync-project', { projectId });
  };

  const handleViewInBasecamp = (project: BasecampProject) => {
    // Use app_url (web UI) instead of url (API endpoint)
    window.open(project.app_url, '_blank');
  };

  const handleAddProject = () => {
    onAction?.('add-project', {});
  };

  // Check if error is auth-related
  const needsAuth = error && (
    error.status === 401 ||
    error.statusCode === 401 ||
    (typeof error === 'string' && (
      error.includes('401') ||
      error.includes('Unauthorized') ||
      error.includes('authentication') ||
      error.includes('auth')
    ))
  );

  // Extract auth_url from error (could be nested in response.data)
  const authUrl = error?.auth_url || error?.response?.data?.auth_url || error?.data?.auth_url;

  if (needsAuth) {
    return (
      <div className="flex-1 overflow-y-auto min-h-0 p-6">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="rounded-full bg-green-500/10 p-3">
              <AlertCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-sm mb-2">Connect to Basecamp</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Authenticate with Basecamp to access your projects and todos
            </p>
          </div>

          <Button
            onClick={async () => {
              // Use extracted authUrl from error, or fetch from auth status
              let urlToOpen = authUrl;

              // If not in error, try to get from auth status
              if (!urlToOpen) {
                try {
                  const { basecampApi } = await import('@/lib/basecamp-api');
                  const status = await basecampApi.getAuthStatus();
                  urlToOpen = status.auth_url;
                } catch (err) {
                  console.warn('[BasecampSidebar] Failed to get auth status:', err);
                }
              }

              // Fallback to default if still no URL
              if (!urlToOpen) {
                const { API_BASE_URL } = await import('@/lib/api');
                urlToOpen = `${API_BASE_URL}/auth/basecamp`;
              }

              // Open in new tab to preserve FlowNotes session
              console.log('[BasecampSidebar] Opening OAuth URL:', urlToOpen);
              const authWindow = window.open(urlToOpen, '_blank');

              if (!authWindow) {
                console.error('[BasecampSidebar] Failed to open OAuth window - popup blocked?');
                // Fallback: try to navigate in same window
                window.location.href = urlToOpen;
              }
            }}
            size="sm"
            className="w-full bg-green-600 hover:bg-green-700"
          >
            Connect to Basecamp
          </Button>

          <p className="text-xs text-muted-foreground">
            Opens in a new tab. Return here after authenticating.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto min-h-0 flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading projects...</div>
      </div>
    );
  }

  if (error && !needsAuth) {
    return (
      <div className="flex-1 overflow-y-auto min-h-0 p-4">
        <div className="text-sm text-red-500">Failed to load projects</div>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
          Try again
        </Button>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto min-h-0 p-4">
        <div className="text-sm text-muted-foreground text-center">
          No projects found in your Basecamp account
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 overflow-hidden">
      {/* Resizable Widgets */}
      <PanelGroup direction="vertical" className="h-full" onLayout={setWidgetSizes}>
        {/* Bookmarks Widget */}
        <Panel defaultSize={widgetSizes[0] || 30} minSize={20} maxSize={50}>
          <BookmarksWidget />
        </Panel>

        <PanelResizeHandle className="h-1 bg-border hover:bg-border/80 transition-colors flex items-center justify-center group">
          <div className="w-8 h-0.5 bg-muted-foreground/20 group-hover:bg-muted-foreground/40 rounded transition-colors" />
        </PanelResizeHandle>

        {/* My Stuff Section */}
        <Panel defaultSize={widgetSizes[1] || 35} minSize={20} maxSize={50}>
          <BasecampMyStuffSidebar />
        </Panel>

        <PanelResizeHandle className="h-1 bg-border hover:bg-border/80 transition-colors flex items-center justify-center group">
          <div className="w-8 h-0.5 bg-muted-foreground/20 group-hover:bg-muted-foreground/40 rounded transition-colors" />
        </PanelResizeHandle>

        {/* Projects Section */}
        <Panel defaultSize={widgetSizes[2] || 35} minSize={20}>
          <div className="flex-shrink-0 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Projects Header */}
        <div className="px-3 py-2 flex items-center justify-between flex-shrink-0">
          <button
            onClick={() => setIsProjectsExpanded(!isProjectsExpanded)}
            className="flex-1 flex items-center gap-2 hover:bg-accent/50 rounded px-2 py-1 -mx-2 -my-1"
          >
            {isProjectsExpanded ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Projects
            </h3>
            <Badge variant="secondary" className="text-xs">
              {activeProjects.length}
            </Badge>
          </button>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => refetch()}>
              <RefreshCw className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleAddProject}>
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Search/Filter Bar */}
        {isProjectsExpanded && (
          <div className="px-3 py-2 border-t border-border flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search all projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-7 pl-7 pr-7 text-xs"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-7 w-7 hover:bg-transparent"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </Button>
            )}
          </div>
          {searchQuery && (
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Searching all projects (including hidden)
            </p>
          )}
          </div>
        )}

        {/* Scrollable Projects List */}
        {isProjectsExpanded && (
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-2">
            {/* Active Projects */}
            {activeProjects.length > 0 && (
              <div className="space-y-2">
                {activeProjects
                  .sort((a, b) => {
                    // Sort pinned projects first (ensure string comparison)
                    const aIdStr = String(a.id);
                    const bIdStr = String(b.id);
                    const aPinned = pinnedProjects.includes(aIdStr);
                    const bPinned = pinnedProjects.includes(bIdStr);

                    // Debug logging for first few comparisons
                    if (Math.random() < 0.1) { // Log ~10% of comparisons to avoid spam
                      console.log('[BasecampSidebar] Sort comparison:', {
                        aId: a.id,
                        aIdStr,
                        aPinned,
                        bId: b.id,
                        bIdStr,
                        bPinned,
                        pinnedProjects
                      });
                    }
                    if (aPinned && !bPinned) return -1;
                    if (!aPinned && bPinned) return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((project) => {
                    const projectIdStr = String(project.id);
                    const isPinned = pinnedProjects.includes(projectIdStr);

                    // Debug logging for pin status check
                    if (Math.random() < 0.05) { // Log ~5% of checks to avoid spam
                      console.log('[BasecampSidebar] Pin status check:', {
                        projectId: project.id,
                        projectIdStr,
                        projectIdType: typeof project.id,
                        isPinned,
                        pinnedProjects,
                        pinnedProjectsTypes: pinnedProjects.map(id => ({ id, type: typeof id }))
                      });
                    }
                    const isHidden = hiddenProjects.includes(project.id);

                    return (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        isPinned={isPinned}
                        isHidden={isHidden}
                        isSearchResult={!!searchQuery.trim()}
                        onClick={(e) => handleProjectClick(project, e)}
                        onPin={() => handlePinProject(project.id)}
                        onHide={() => handleHideProject(project.id)}
                        onShow={() => handleShowProject(project.id)}
                        onSync={() => handleSyncProject(project.id)}
                        onViewInBasecamp={() => handleViewInBasecamp(project)}
                      />
                    );
                  })}
              </div>
            )}

            {/* Hidden Projects Section - Only show when not searching */}
            {!searchQuery && hidden.length > 0 && (
              <>
                <Separator className="my-4" />
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground px-2">
                    Hidden Projects ({hidden.length})
                  </h4>
                  {hidden.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-2 rounded-md hover:bg-accent text-xs"
                    >
                      <span className="text-muted-foreground truncate">{project.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShowProject(project.id)}
                        className="h-6 px-2"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Show
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          </div>
        )}
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
}

// Separate ProjectCard component with real data fetching
function ProjectCard({
  project,
  isPinned,
  isHidden = false,
  isSearchResult = false,
  onClick,
  onPin,
  onHide,
  onShow,
  onSync,
  onViewInBasecamp
}: {
  project: BasecampProject;
  isPinned: boolean;
  isHidden?: boolean;
  isSearchResult?: boolean;
  onClick: (e: React.MouseEvent) => void;
  onPin: () => void;
  onHide: () => void;
  onShow?: () => void;
  onSync: () => void;
  onViewInBasecamp: () => void;
}) {
  const OpenInBasecampIcon = resolveOpenExternalIcon(BasecampIcon)
  // Fetch stats from cache (no API calls - reads from database)
  const { stats: projectStats } = useBasecampProjectStats(project.id);

  const stats = {
    overdue: projectStats?.overdue ?? 0,
    active: projectStats?.active ?? 0,
  };

  return (
    <div className="group relative">
      <div
        onClick={onClick}
        className="w-full p-3 rounded-md hover:bg-accent text-left border border-border transition-colors cursor-pointer relative"
      >
        {/* Pinned accent dot - 4px, absolutely positioned to match tab style */}
        {isPinned && (
          <div
            className="absolute top-1 left-1.5 rounded-full z-10 pointer-events-none bg-[hsl(var(--tab-pinned-accent))]"
            style={{
              width: '4px',
              height: '4px',
            }}
            title="Pinned project"
          />
        )}

        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">
                {project.name}
              </span>
              {isHidden && isSearchResult && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                  Hidden
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2 mt-1">
              {stats.overdue > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {stats.overdue} overdue
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                {stats.active} active
              </span>
            </div>

            <div className="text-xs text-muted-foreground mt-1">
              Last sync: {project.lastSync ? formatLastSync(project.lastSync) : 'Never synced'}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-60 hover:opacity-100 transition-opacity"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
              collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
            >
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPin(); }}>
                <Pin className="h-4 w-4 mr-2" />
                {isPinned ? 'Unpin' : 'Pin'} Project
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); }}>
                <Eye className="h-4 w-4 mr-2" />
                Monitor for Assignments
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSync(); }}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync Now
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewInBasecamp(); }}>
                <OpenInBasecampIcon className="h-4 w-4 mr-2" />
                {BASECAMP_OPEN_IN_BASECAMP_LABEL}
              </DropdownMenuItem>
              {isHidden ? (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onShow?.(); }}>
                  <Eye className="h-4 w-4 mr-2" />
                  Show Project
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onHide(); }}>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Hide Project
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

function formatLastSync(lastSync: Date): string {
  const now = new Date();
  const diff = now.getTime() - lastSync.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}
